import { findChildrenByType, NodeWithPos } from 'prosemirror-utils';
import { TableRect, selectedRect } from 'prosemirror-tables';
import {
  ResolvedPos,
  Node as ProsemirrorNode,
  Schema,
  Slice,
  DOMSerializer,
} from 'prosemirror-model';
import { Transaction, NodeSelection, TextSelection, EditorState } from 'prosemirror-state';
import { EditorHelper } from '@/editor/helpers/editor.helper';
import { IAlignList, IField, ITextContentDoc } from '@/editor/types';
import { ENodeAlign, EProsemirrorType } from '@/editor/types/enums';
import { IText, MergeTagNode } from '@/editor/types/text-content';

export class ProsemirrorHelper {
  static selectNode(transaction: Transaction, position: number): Transaction {
    return transaction.setSelection(NodeSelection.create(transaction.doc, position));
  }

  static selectTextNode(transaction: Transaction, position: number): Transaction {
    return transaction.setSelection(TextSelection.create(transaction.doc, position));
  }

  static selectTextNearPosition(transaction: Transaction, position: number): Transaction {
    const resolvedPosition = transaction.doc.resolve(position);
    return transaction.setSelection(TextSelection.near(resolvedPosition));
  }

  static deleteNode(transaction: Transaction, position: number, nodeSize: number): Transaction {
    return transaction.delete(position, position + nodeSize);
  }

  static createEditorStateFromSlice(slice: Slice, schema: Schema): EditorState {
    const state: EditorState = EditorState.create({ schema });
    const transaction: Transaction = state.tr.replace(0, state.doc.content.size, slice);
    return state.apply(transaction);
  }

  static isCompleteTableSelected(state: EditorState): boolean {
    const { left, right, map, top, bottom }: TableRect = selectedRect(state);
    return left === 0 && right === map.width && top === 0 && map.height === bottom;
  }

  static getNodeAttributesFromPosition(
    transaction: Transaction,
    position: number
  ): ProsemirrorNode['attrs'] {
    const node: ProsemirrorNode = transaction.doc.nodeAt(position);

    if (!node) {
      return;
    }

    return node.attrs;
  }

  static getNodeBeforePosition(transaction: Transaction, position: number): ProsemirrorNode {
    const resolvedPosition: ResolvedPos = transaction.doc.resolve(position);
    return resolvedPosition.nodeBefore;
  }

  static getNodeAfterPosition(transaction: Transaction, position: number): ProsemirrorNode {
    const resolvedPosition: ResolvedPos = transaction.doc.resolve(position);
    return resolvedPosition.nodeAfter;
  }

  static updateNodeAttributes(
    transaction: Transaction,
    schema: Schema,
    position: number,
    nodeType: EProsemirrorType,
    attributes: ProsemirrorNode['attrs']
  ): Transaction {
    const attrs: ProsemirrorNode['attrs'] = ProsemirrorHelper.getNodeAttributesFromPosition(
      transaction,
      position
    );

    if (!attrs) {
      return;
    }

    return transaction.setNodeMarkup(position, schema.nodes[nodeType], {
      ...attrs,
      ...attributes,
    });
  }

  static alignList(
    state: EditorState,
    { align, position }: IAlignList,
    type: EProsemirrorType
  ): Transaction {
    const { tr, schema, doc } = state;
    const listNode: ProsemirrorNode = doc.nodeAt(position);

    if (!listNode) {
      return;
    }

    const paragraphs: NodeWithPos[] = findChildrenByType(
      listNode,
      schema.nodes[EProsemirrorType.Paragraph]
    );
    const headings: NodeWithPos[] = findChildrenByType(
      listNode,
      schema.nodes[EProsemirrorType.Heading]
    );

    [...paragraphs, ...headings].forEach(({ pos, node }: NodeWithPos) => {
      const nodePosition = position + pos + 1;
      const nodeAttributes = ProsemirrorHelper.getNodeAttributesFromPosition(tr, nodePosition);

      tr.setNodeMarkup(nodePosition, schema.nodes[node.type.name], { ...nodeAttributes, align });
    });

    return tr.setNodeMarkup(position, schema.nodes[type], {
      align,
    });
  }

  static getLinkStartEndPos(state: EditorState, pos: number): { from: number; to: number } {
    const $pos = state.doc.resolve(pos);

    const { parent, parentOffset } = $pos;
    const start = parent.childAfter(parentOffset);
    if (!start.node) return null;

    const link = start.node.marks.find(mark => mark.type === state.schema.marks.link);
    if (!link) return null;

    let startIndex = $pos.index();
    let startPos = $pos.start() + start.offset;
    let endIndex = startIndex + 1;
    let endPos = startPos + start.node.nodeSize;
    while (startIndex > 0 && link.isInSet(parent.child(startIndex - 1).marks)) {
      startIndex -= 1;
      startPos -= parent.child(startIndex).nodeSize;
    }
    while (endIndex < parent.childCount && link.isInSet(parent.child(endIndex).marks)) {
      endPos += parent.child(endIndex).nodeSize;
      endIndex += 1;
    }
    return { from: startPos, to: endPos };
  }

  static getTagIds(content = ''): string[] {
    if (!content) {
      return [];
    }
    return content
      .split('"tagId":"')
      .slice(1)
      .map(id => id.substring(0, EditorHelper.NANO_ID_LENGTH));
  }

  static replaceBrNodes(fromTransaction: Transaction, schema: Schema): Transaction {
    let transaction = fromTransaction;

    const brNodes: NodeWithPos[] = findChildrenByType(
      transaction.doc,
      schema.nodes[EProsemirrorType.HardBreak]
    ).reverse();

    brNodes.forEach(({ node, pos }: NodeWithPos) => {
      transaction = transaction.replaceWith(
        pos,
        pos + node.nodeSize,
        schema.text('\n', node.marks)
      );
    });

    return transaction;
  }

  static getStringFromTransaction(transaction: Transaction, schema: Schema): string {
    // doc.content.firstChild.content - targets content of the root paragraph element
    const fragment = DOMSerializer.fromSchema(schema).serializeFragment(
      transaction.doc.content.firstChild.content
    );
    const container = document.createElement('div');
    container.appendChild(fragment);
    return container.innerText;
  }

  static replaceMergeNodes(fromTransaction: Transaction, schema: Schema): Transaction {
    let transaction = fromTransaction;

    const nodes: NodeWithPos[] = findChildrenByType(
      transaction.doc,
      schema.nodes[EProsemirrorType.MergeTag]
    ).reverse();

    nodes.forEach(({ node, pos }: NodeWithPos) => {
      const mergeId = node.attrs.tagId;

      if (mergeId) {
        transaction = transaction.replaceWith(
          pos,
          pos + node.nodeSize,
          schema.text(`{{${mergeId}}}`, node.marks)
        );
      }
    });

    return transaction;
  }

  private static makeMergeTagNode(mergeTag: string, fields: IField[]): MergeTagNode {
    const tagId = mergeTag.substr(2, EditorHelper.NANO_ID_LENGTH);

    const { category, mergeKey, customName } = fields.find(field => field.id === tagId) || {
      category: null,
      customName: null,
      mergeKey: null,
    };

    return {
      type: EProsemirrorType.MergeTag,
      attrs: {
        category,
        customName,
        mergeKey,
        tagId,
      },
    };
  }

  private static makeTextNode(text: string): IText {
    return {
      type: EProsemirrorType.Text,
      text: text.replace(/(\n)+/g, '<br>'),
    };
  }

  static getInputContent(value: string, fields: IField[]): ITextContentDoc {
    const mergeTagExpression = new RegExp(`{{.{${EditorHelper.NANO_ID_LENGTH}}}}`);
    const newline = '\n';
    const tagOrNewlineExpression = new RegExp(`(${mergeTagExpression.source}|${newline})`);

    return {
      type: EProsemirrorType.Doc,
      content: [
        {
          attrs: { align: ENodeAlign.Left },
          type: EProsemirrorType.Paragraph,
          content: value
            .split(tagOrNewlineExpression)
            .filter(node => node)
            .map(node => {
              if (mergeTagExpression.test(node)) {
                return ProsemirrorHelper.makeMergeTagNode(node, fields);
              }

              if (node === newline) {
                return { type: EProsemirrorType.HardBreak };
              }

              return ProsemirrorHelper.makeTextNode(node);
            }),
        },
      ],
    };
  }
}
